/**
 * StepZipCode.js
 *
 * An onboarding step that collects the user's ZIP code.
 * It uses the ZIP code to search for active communities and sites.
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import _ from 'lodash';
import useSiteSearch from '@/hooks/useSiteSearch';
import validators from '@/utils/validators';
import FormErrors from '@/forms/_common/FormErrors';
import ErrorMessage from '@/forms/_common/ErrorMessage';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import Overlay from '@/forms/_common/Overlay';
import RequestError from '@/forms/_common/RequestError';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import ZipCodeBody from './ZipCodeBody';

/**
 * The ZIP code selection step in the onboarding wizard.
 *
 * @returns {JSX.Element} The rendered ZIP code step.
 */
export default function StepZipCode() {
  const { saving, loading, error, request, save, refetchUser, registerOnBeforeNext, setNextDisabled, data } = useOnboarding();

  const [zipData, setZipData] = useState(_.pick(data, ['zipCode']));
  const initialZipData = useRef(zipData);
  const [debouncedZipCode, setDebouncedZipCode] = useState(zipData.zipCode);
  const [showErrors, setShowErrors] = useState(false);

  const debouncedSetZipCode = useMemo(() => _.debounce((value) => {
    setDebouncedZipCode(value);
  }, 500), []);

  // Fetch site count and location data based on the entered ZIP code
  const { total: siteCount, city, state, isLoading: isSearching } = useSiteSearch({
    zipCode: debouncedZipCode,
    isActive: true,
    countOnly: true,
    enabled: debouncedZipCode?.length >= 5,
  });

  /**
   * Handles changes to form fields, specifically the ZIP code.
   *
   * @param {string} name - The name of the field.
   * @param {*} value - The new value of the field.
   */
  function onChange(name, value) {
    if (name === 'zipCode' && value.length > 5) {
      return;
    }
    setZipData(_.merge({}, zipData, { [name]: value }));
    if (name === 'zipCode') {
      debouncedSetZipCode(value);
    }
  }

  useEffect(() => {
    setNextDisabled(zipData.zipCode?.length !== 5 || isSearching);
  }, [zipData.zipCode, isSearching, setNextDisabled]);

  useEffect(() => {
    registerOnBeforeNext(async () => {
      const hasError = !!validators.isRequired(zipData.zipCode);
      if (hasError) {
        setShowErrors(true);
        return Promise.resolve(false);
      }
      if (_.isEqual(zipData, initialZipData.current)) {
        return true;
      }
      const success = await save(zipData);
      if (success) {
        refetchUser();
        return true;
      }
      return false;
    });
  }, [zipData, registerOnBeforeNext, save, refetchUser]);

  return (
    <div>
      <Overlay isVisible={loading || saving}>
        <FormErrors data={zipData} validators={{
          zipCode: [validators.isRequired],
        }}>
          {({ errors }) => (
            <FormTemplate
              title="Where would you like to get involved?"
              subtitle="Enter the ZIP code for the community where you work, live, or would like to connect."
              alert={error ? (
                <RequestError request={request} v2={true} />
              ) : showErrors ? (
                <ErrorMessage v2={true}>
                  Please fill in all required fields.
                </ErrorMessage>
              ) : null}
              body={(
                <ZipCodeBody
                  zipData={zipData}
                  debouncedZipCode={debouncedZipCode}
                  isSearching={isSearching}
                  city={city}
                  state={state}
                  siteCount={siteCount}
                  showErrors={showErrors}
                  errors={errors}
                  onChange={onChange}
                />
              )}
            />
          )}
        </FormErrors>
      </Overlay>
    </div>
  );
}
